import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spinner } from "@material-tailwind/react";
import { portalApi } from "../../services/portalApi";

export default function AdminRevistasPanel() {
  const [issues, setIssues] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [iss, subs] = await Promise.all([
        portalApi.adminRevistaIssues(),
        portalApi.adminRevistaSubscribers({ status: "active", limit: 50, offset: 0 }),
      ]);
      setIssues(iss.issues || []);
      setSubscribers(subs.subscribers || []);
      setSubTotal(subs.total ?? 0);
    } catch (err) {
      setError(err?.message || "Error al cargar Conexión Siete");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-brand-gray">
        <Spinner className="h-5 w-5" /> Cargando Conexión Siete…
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600 text-sm py-6">{error}</p>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-brand-gray">
          Suscriptores activos: <strong className="text-brand-dark">{subTotal}</strong>
        </p>
        <Link to="/admin/revistas/nuevo">
          <Button size="sm" className="bg-brand-lavender text-white normal-case">
            Nuevo ejemplar
          </Button>
        </Link>
      </div>

      <div className="overflow-x-auto rounded-xl border border-brand-lavender/20 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-brand-lilac/30 text-left text-brand-navy">
            <tr>
              <th className="px-4 py-3 font-semibold">Título</th>
              <th className="px-4 py-3 font-semibold">Estado</th>
              <th className="px-4 py-3 font-semibold">PDF</th>
              <th className="px-4 py-3 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {issues.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-brand-gray text-center">
                  No hay ejemplares.{" "}
                  <Link to="/admin/revistas/nuevo" className="text-brand-lavender hover:underline">
                    Crea el primero
                  </Link>
                  .
                </td>
              </tr>
            ) : (
              issues.map((iss) => (
                <tr key={iss.issueId} className="border-t border-brand-lavender/15">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/revistas/${iss.issueId}`}
                      className="font-medium text-brand-dark hover:text-brand-lavender hover:underline"
                    >
                      {iss.title}
                    </Link>
                    {iss.publishedAt ? (
                      <div className="text-xs text-brand-gray mt-0.5">
                        Pub. {iss.publishedAt}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 capitalize">{iss.status || "—"}</td>
                  <td className="px-4 py-3">
                    {iss.s3Key ? (
                      iss.downloadUrl ? (
                        <Link
                          to={`/revistas/leer?u=${encodeURIComponent(iss.downloadUrl)}`}
                          className="text-brand-lavender hover:underline"
                        >
                          Leer
                        </Link>
                      ) : (
                        "Subido"
                      )
                    ) : (
                      <span className="text-brand-gray">Pendiente</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/revistas/${iss.issueId}`}
                      className="text-brand-lavender hover:underline"
                    >
                      {iss.status === "published" ? "Ver" : "Editar"}
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-brand-dark mb-2">
          Últimos suscriptores (activo)
        </h3>
        <ul className="text-sm text-brand-gray space-y-1 max-h-48 overflow-y-auto">
          {subscribers.length === 0 ? (
            <li>Ninguno aún.</li>
          ) : (
            subscribers.map((s) => (
              <li key={s.email}>
                {s.name} · {s.email}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
